import * as d3 from 'd3'
import moment from 'moment-timezone'

import { ChartView } from '../ChartView'

/**
 * Class which represents a BarChart model used in the BarChart vue component
 */
export class BatteryLevelChartView extends ChartView {
  constructor(chartId, dataset, chartType, size, margin) {
    super(chartId, dataset, chartType, size, margin)
    this.timeProperty = 'time'

    this.dataRanges = {
      level_points: [
        {
          type: 'good',
          range: [100, 70],
          color: '#50f186',
        },
        {
          type: 'medium',
          range: [70, 50],
          color: 'yellow',
        },
        {
          type: 'bad',
          range: [50, 30],
          color: '#eed42b',
        },
        {
          type: 'verybad',
          range: [30, 0],
          color: '#ef4b39',
        },
      ],
      temperature_points: [
        {
          type: 'good',

          range: [20, 0],
          color: '#90F494',
        },
        {
          type: 'medium',
          range: [30, 20],
          color: '#C7E251',
        },
        {
          type: 'bad',
          range: [40, 30],
          color: '#F18D28',
        },
        {
          type: 'verybad',
          range: [100, 40],
          color: '#D8493E',
        },
      ],
    }
  }

  /**
   *
   */
  draw() {
    this.dataset = JSON.parse(JSON.stringify(this.dataset))
    this.dataset.levels = this.dataset.levels.map(level => {
      level.values = level.values.map(value => {
        value.dateTime = moment(value.dateTime).toDate()
        return value
      })
      return level
    })
    if (this.config.display_options.range && this.config.display_options.range !== 'all') {
      this.filterDatasetForRange()
    }

    //Creating create battery level chart according to the current dataset
    this.createChart()
  }

  createChart() {
    const that = this
    moment.tz.setDefault('UTC')

    // Scale functions
    this.x = this.config.display_options.over_day
      ? d3.scaleTime().range([0, this.width])
      : d3.scaleLinear().range([0, this.width])
    this.y = d3.scaleLinear().range([this.height, 0])

    this.timeProperty = this.config.display_options.over_day ? 'dateTime' : 'time'

    // Concatenate each date times for each session battery level logs
    let dateTimes = []
    for (const sessionBatteryLevel of this.dataset.levels) {
      dateTimes = dateTimes.concat(
        sessionBatteryLevel.values.map(value => {
          return value.dateTime
        }),
      )
    }

    //If chart is zoomed
    if (this.config.display_options.zoomed) {
      if (this.config.display_options.over_day) {
        that.x.domain(
          d3.extent(dateTimes, d => {
            return d
          }),
        )
      } else {
        that.x.domain([
          0,
          d3.max(this.dataset.levels, function(d) {
            return d3.max(d.values, function(d2) {
              return d2[that.timeProperty]
            })
          }),
        ])
      }

      that.y.domain([
        d3.min(this.dataset.levels, function(d) {
          return d3.min(d.values, function(d2) {
            return d2.level
          })
        }),
        d3.max(this.dataset.levels, function(d) {
          return d3.max(d.values, function(d2) {
            return d2.level
          })
        }),
      ])
    } else {
      //If not
      if (this.config.display_options.over_day) {
        that.x.domain(
          d3.extent(dateTimes, d => {
            return d
          }),
        )
      } else {
        that.x.domain([0, 400])
      }
      that.y.domain([0, that.dataset.type == 'temperature_points' ? 60 : 100])
    }

    //Axis initialization
    this.xAxis = d3
      .axisBottom()
      .scale(that.x)
      .ticks(20)
      .tickSizeInner(-this.height)
      .tickSizeOuter(3)

    this.yAxis = d3
      .axisLeft()
      .scale(that.y)
      .ticks(20)
      .tickSizeInner(-this.width)
      .tickSizeOuter(3)

    // Value line use to display line path in the chart for each battery information
    this.valueline = d3
      .line()
      .defined(function(d) {
        return d
      })
      .x(function(d) {
        return that.x(d[that.timeProperty])
      })
      .y(function(d) {
        return that.y(d.level)
      })

    //Gradient infos use to display gradient
    let gradientInfos = new Array()

    this.svg.append('defs')
    //Calculate color gradients according to data
    this.dataset.levels.forEach((line, l) => {
      let gradientArray = new Array()
      let gradientArrayAll = new Array()
      let previousGradient
      line.values.forEach((point, i, array) => {
        if (i + 1 < array.length) {
          let p1 = array[i]
          let p2 = array[i + 1]
          if (p2.time - p1.time != 0) {
            let gradient = (p2.level - p1.level) / (p2.time - p1.time)
            let offset = (array[i].time * 100) / array[array.length - 1].time
            gradientArrayAll.push({
              offset: offset,
              p1: p1,
              p2: p2,
              gradient: gradient,
              color: this.gradientBatteryColor(gradient, this.dataset.type),
              level: p2.level - p1.level,
            })
            if (i > 0) {
              gradientArray.push({
                offset: offset + '%',
                color: this.gradientBatteryColor(previousGradient, this.dataset.type),
              })
            }
            gradientArray.push({
              offset: offset + '%',
              color: this.gradientBatteryColor(gradient, this.dataset.type),
            })
            previousGradient = gradient
          }
        }
      })
      gradientInfos.push(gradientArrayAll)

      this.svg
        .select('defs')
        .append('linearGradient')
        .attr('id', this.chartId + '-area-gradient-' + l)
        .attr('session', line.label)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '0%')
        .selectAll('stop')
        .data(gradientArray)
        .enter()
        .append('stop')
        .attr('offset', function(d) {
          return d.offset
        })
        .attr('stop-color', function(d) {
          return d.color
        })
    })

    //Add ceils rectangle
    this.svg
      .selectAll('.ceil-rect')
      .data(this.dataRanges[this.dataset.type])
      .enter()
      .append('rect')
      .attr('class', 'ceil-rect')
      .attr('fill', d => {
        return d.color
      })
      .style('opacity', 0.3)
      .attr('x', 0)
      .attr('y', d => {
        return that.y(d.range[0])
      })
      .attr('width', this.width)
      .attr('height', d => {
        let height = that.y(d.range[1]) - that.y(d.range[0])
        if (height + that.y(d.range[0]) > that.height) {
          height = that.height - that.y(d.range[0])
        }
        return height
      })

    this.svg
      .selectAll('line')
      .data(this.dataset.levels)
      .enter()
      .append('path')
      .attr('class', 'battery-line')
      .style('stroke-width', '2px')
      .attr('id', function(d) {
        return 'line-' + d.session_id
      })
      .attr('stroke', function(array, d) {
        return 'url(#' + that.chartId + '-area-gradient-' + d + ')'
      })
      .attr('d', function(d) {
        return that.valueline(d.values)
      })
      .attr('fill', 'none')
      .on('mouseover', function(d) {
        that.svg.selectAll('.battery-line').attr('opacity', 0.0)
        that.svg.selectAll('.circle2').attr('opacity', 0.0)
        that.svg.selectAll('.cross').attr('opacity', 0.0)

        d3.select(this).attr('opacity', 1.0)
        d3.select('#circle-' + d.session_id).attr('opacity', 1.0)
        d3.select('#cross1-' + d.session_id).attr('opacity', 1.0)
        d3.select('#cross2-' + d.session_id).attr('opacity', 1.0)

        d3.select('#batteryInformationLabel').html(
          `Session n° <strong>${d.session_id}</strong>, serial: <strong>${d.device_serial}</strong>`,
        )
      })
      .on('mouseout', function() {
        that.svg.selectAll('.battery-line').attr('opacity', 1.0)
        that.svg.selectAll('.circle2').attr('opacity', 1.0)
        that.svg.selectAll('.cross').attr('opacity', 1.0)
        d3.select('#batteryInformationLabel').html(`&nbsp;`)
      })
      .on('click', function(d) {
        that.onLineClickHandler(d.session_id)
      })

    const crashLines = JSON.parse(
      JSON.stringify(
        this.dataset.levels.filter(level => {
          return level.is_crash
        }),
      ),
    )

    crashLines.forEach(line => {
      line.values = [line.values[line.values.length - 1]]
      line.values = line.values.map(value => {
        value.dateTime = moment(value.dateTime).toDate()
        return value
      })
      line.values[0]['session_id'] = line.session_id

      this.svg
        .selectAll('.circle')
        .data(line.values)
        .enter()
        .append('circle')
        .attr('id', function() {
          return 'circle-' + line.session_id
        })
        .attr('class', 'circle2')
        .attr('fill', function() {
          return 'red'
        })
        .attr('r', 4)
        .attr('cx', function(d) {
          return that.x(d[that.timeProperty])
        })
        .attr('cy', function(d) {
          return that.y(d.level)
        })
        .on('mouseover', function(d) {
          that.svg.selectAll('.battery-line').attr('opacity', 0.0)
          that.svg.selectAll('.circle2').attr('opacity', 0.0)

          d3.select(this).attr('opacity', 1.0)
          d3.select('#line-' + d.session_id).attr('opacity', 1.0)

          d3.select('#batteryInformationLabel').html(
            `Session n° <strong>${d.session_id}</strong>, serial: <strong>${d.device_serial}</strong>`,
          )
        })
        .on('mouseout', function() {
          that.svg.selectAll('.battery-line').attr('opacity', 1.0)
          that.svg.selectAll('.circle2').attr('opacity', 1.0)
          d3.select('#batteryInformationLabel').html(``)
        })
        .on('click', function(d) {
          that.onLineClickHandler(d.session_id, d.log_version_major)
        })

      this.svg
        .selectAll('.circle')
        .data(line.values)
        .enter()
        .append('line')
        .attr('id', function() {
          return 'cross1-' + line.session_id
        })
        .attr('class', 'cross')
        .attr('x1', function(d) {
          return that.x(d[that.timeProperty]) - 2
        })
        .attr('y1', function(d) {
          return that.y(d.level) - 2
        })
        .attr('x2', function(d) {
          return that.x(d[that.timeProperty]) + 2
        })
        .attr('y2', function(d) {
          return that.y(d.level) + 2
        })
        .style('stroke', 'white')
        .style('stroke-width', '2px')

      this.svg
        .selectAll('.circle')
        .data(line.values)
        .enter()
        .append('line')
        .attr('id', function() {
          return 'cross2-' + line.session_id
        })
        .attr('class', 'cross')
        .attr('x1', function(d) {
          return that.x(d[that.timeProperty]) - 2
        })
        .attr('y1', function(d) {
          return that.y(d.level) + 2
        })
        .attr('x2', function(d) {
          return that.x(d[that.timeProperty]) + 2
        })
        .attr('y2', function(d) {
          return that.y(d.level) - 2
        })
        .style('stroke', 'white')
        .style('stroke-width', '2px')
    })

    // Add the X Axis
    this.svg
      .append('g')
      .attr('class', 'chart-axis')
      .style('stroke-width', '2px')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(this.xAxis)

    // Add the Y Axis
    this.svg
      .append('g')
      .attr('class', 'chart-axis')
      .style('stroke-width', '2px')
      .call(this.yAxis)

    d3.selectAll('.chart-axis line').attr('class', 'tick-line')
  }

  setDisplayOptions(displayOptions) {
    this.displayOptions = displayOptions
  }

  gradientBatteryColor(gradient, dataType) {
    if (dataType == 'level_points') {
      if (gradient < -0.5) {
        return '#ef4b39'
      } else if (gradient < -0.3) {
        return '#eed42b'
      } else if (gradient < -0.1) {
        return '#50f186'
      } else if (gradient < 0) {
        return '#50f186'
      } else if (gradient >= 0 && gradient <= 1) {
        return '#50f186'
      } else if (gradient > 1) {
        return '#50f186'
      }
    } else if (dataType == 'temperature_points') {
      if (gradient < -0.5) {
        return '#60FBDA'
      } else if (gradient < -0.3) {
        return '#90F494'
      } else if (gradient < -0.1) {
        return '#B9E862'
      } else if (gradient < 0.0) {
        return '#C7E251'
      } else if (gradient < 0.2) {
        return '#EDD82F'
      } else if (gradient >= 0.2 && gradient <= 0.3) {
        return '#F18D28'
      } else if (gradient > 0.3) {
        return '#D8493E'
      }
    } else {
      return 'blue'
    }
  }

  /**
   * Set on click handler to each displayed battery levels / temperatures
   * @param {*} handler
   */
  setOnLineClickHandler(handler) {
    this.onLineClickHandler = handler
  }

  /**
   * Function used to filters battery levels / temperatures according to the selected ceils range
   */
  filterDatasetForRange() {
    const dataRange = this.dataRanges[this.dataset.type].find(dataRange => {
      return dataRange.type === this.config.display_options.range
    })
    this.dataset.levels = this.dataset.levels.filter(level => {
      let values = JSON.parse(JSON.stringify(level.values))
      values = values.sort((a, b) => {
        return a.level - b.level
      })

      const value =
        this.dataset.type === 'temperature_points' ? values[values.length - 1] : values[0]

      if (value.level >= dataRange.range[1] && value.level <= dataRange.range[0]) {
        return true
      } else {
        return false
      }
    })
  }
}
